if (!customElements.get('wlsn-blog-facet')) {
  class WLSNBlogFacet extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.isLoading = false;

      this.classes = {
        loading: 'loading',
        facet: 'wlsn-blog-facet',
        active: 'active',
        wlsnMainBlogNavTabs: 'wlsn-main-blog-nav-tabs',
        wlsnBlogFeatured: 'wlsn-blog-featured',
        mainBlogTabLink: 'js-main-blog-tab-link',
      };

      this.bindMethods = {
        onPaginationChange: this._onPaginationChange.bind(this),
        onTabClick: this._onTabClick.bind(this),
      };
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        wlsnMainBlogNavTabs: document.querySelector(`${this.classes.wlsnMainBlogNavTabs}`),
        wlsnBlogFeatured: document.querySelector(`${this.classes.wlsnBlogFeatured}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this.addEventListener('wlsn:pagination:page-changed', this.bindMethods.onPaginationChange);
      if (this.elements.wlsnMainBlogNavTabs) {
        document.addEventListener('wlsn:main-blog-nav-tabs:tab-clicked', this.bindMethods.onTabClick);
      }
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      this.removeEventListener('wlsn:pagination:page-changed', this.bindMethods.onPaginationChange);
      if (this.elements.wlsnMainBlogNavTabs) {
        document.removeEventListener('wlsn:main-blog-nav-tabs:tab-clicked', this.bindMethods.onTabClick);
      }
    }

    /**
     * Handle tab click event
     * @param {Event} event - The tab click event
     * @returns {void}
     */
    _onTabClick(event) {
      const url = new URL(event.detail.url);
      this._fetchAndUpdate(url);
    }

    /**
     * Handle pagination change event
     * @param {Event} event - The pagination change event
     * @returns {Promise<void>}
     */
    async _onPaginationChange(event) {
      const url = new URL(event.detail.url);
      this._fetchAndUpdate(url, true);
    }

    /**
     * Fetch and update content from URL
     * @param {URL} url - The URL to fetch content from
     * @param {boolean} [isPagination=false] - Whether this is a pagination request
     * @returns {Promise<void>}
     */
    async _fetchAndUpdate(url, isPagination = false) {
      // Prevent overlapping requests
      if (this.isLoading) {
        this._abort();
      }

      this.isLoading = true;
      this._setLoading();

      try {
        this.abortController = new AbortController();
        const res = await fetch(url.toString(), { signal: this.abortController.signal });
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newFacet = doc.querySelector(`.${this.classes.facet}`);
        const newMainBlogNavTabs = doc.querySelector(`${this.classes.wlsnMainBlogNavTabs}`);
        const newWLSNBlogFeatured = doc.querySelector(`${this.classes.wlsnBlogFeatured}`);

        if (newFacet) {
          this.innerHTML = newFacet.innerHTML;
        }

        if (newMainBlogNavTabs && this.elements.wlsnMainBlogNavTabs) {
          const newActiveLink = newMainBlogNavTabs.querySelector(`.${this.classes.mainBlogTabLink}.${this.classes.active}`);
          this.elements.wlsnMainBlogNavTabs.querySelectorAll(`.${this.classes.mainBlogTabLink}`).forEach((link) => {
            const isActive = newActiveLink && link.href === newActiveLink.href;
            link.classList.toggle(this.classes.active, isActive);
          });
        }

        if (newWLSNBlogFeatured && this.elements.wlsnBlogFeatured) {
          this.elements.wlsnBlogFeatured.replaceWith(newWLSNBlogFeatured);
        }

        history.replaceState({}, '', url);

        if (isPagination) {
          const sectionTop = this.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: sectionTop - 100, behavior: 'smooth' });
        }
        // else {
        //   window.scrollTo({ top: 0, behavior: 'smooth' });
        // }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[WLSNBlogFacet] Fetch error:', error);
        }
      } finally {
        this.isLoading = false;
        this._removeLoading();
      }
    }

    /**
     * Abort current fetch request
     * @returns {void}
     */
    _abort() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    /**
     * Set loading state
     * @returns {void}
     */
    _setLoading() {
      this.classList.add(this.classes.loading);
      if (this.elements.wlsnMainBlogNavTabs) {
        this.elements.wlsnMainBlogNavTabs.classList.add(this.classes.loading);
        this.elements.wlsnMainBlogNavTabs.querySelector('wlsn-nav-tabs').addEventListener('click', (event) => {
          event.preventDefault();
        });
      }
    }

    /**
     * Remove loading state
     * @returns {void}
     */
    _removeLoading() {
      this.classList.remove(this.classes.loading);
      if (this.elements.wlsnMainBlogNavTabs) {
        this.elements.wlsnMainBlogNavTabs.classList.remove(this.classes.loading);
        this.elements.wlsnMainBlogNavTabs.querySelector('wlsn-nav-tabs').removeEventListener('click', (event) => {
          event.preventDefault();
        });
      }
    }
  }

  customElements.define('wlsn-blog-facet', WLSNBlogFacet);
}
